import { useContext, useEffect, useRef, useState } from 'react';
import { SharedCalendarLinkedContext } from '../contexts/calendarLinkedShared';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import {
  ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT,
  type CalendarLinkedContext,
  resolveCalendarLinkedContext,
} from '../utils/calendarSectionAcademicYear';

export const EMPTY_CALENDAR_LINKED_CONTEXT: CalendarLinkedContext = {
  sectionId: 'base',
  academicYear: new Date().getFullYear(),
  terms: [],
  calendarStartsOn: '',
  calendarEndsOn: '',
  useDbCalendarDays: true,
  localCalendarDays: [],
  sectionSettings: null,
};

/**
 * 학사일정 섹션 resolve(네트워크·LS). Provider 또는 `useCalendarLinkedContext`에서만 호출.
 */
export function useCalendarLinkedContextResolved(
  workspaceId: string,
  gradeId: string,
  enabled: boolean,
  options?: { localOnly?: boolean }
): CalendarLinkedContext {
  const fallbackYear = new Date().getFullYear();
  const localOnly = options?.localOnly === true;
  const [ctx, setCtx] = useState<CalendarLinkedContext>(() => ({
    ...EMPTY_CALENDAR_LINKED_CONTEXT,
    academicYear: fallbackYear,
  }));
  const [storageRev, setStorageRev] = useState(0);
  const { setProgramSidebarAcademicYear } = useProgramSidebar();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      const k = e.key ?? '';
      if (k.includes('cp.academicSectionSettings.') || k.includes('cp.academicSectionCalendarDays.')) {
        setStorageRev((r) => r + 1);
      }
    };
    const onSameTabCalendarChange = () => setStorageRev((r) => r + 1);
    window.addEventListener('storage', onStorage);
    window.addEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, onSameTabCalendarChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, onSameTabCalendarChange);
    };
  }, [enabled]);

  const resolveGenRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (!workspaceId) {
      setCtx({ ...EMPTY_CALENDAR_LINKED_CONTEXT, academicYear: fallbackYear });
      return;
    }
    if (!gradeId) return;

    const runId = (resolveGenRef.current += 1);
    void resolveCalendarLinkedContext(workspaceId, gradeId, fallbackYear, {
      localOnly,
    }).then((next) => {
      if (runId !== resolveGenRef.current) return;
      setCtx(next);
      setProgramSidebarAcademicYear(next.academicYear);
    });
    return () => {
      resolveGenRef.current += 1;
    };
  }, [enabled, workspaceId, gradeId, fallbackYear, storageRev, setProgramSidebarAcademicYear, localOnly]);

  return ctx;
}

/**
 * 클래스가 구독하는 학사일정 섹션 기준 학년도·학기·공휴일 소스.
 * MainLayout Provider와 `workspaceId`·`gradeId`가 같으면 resolve를 공유합니다.
 */
export function useCalendarLinkedContext(
  workspaceId: string,
  gradeId: string,
  options?: { localOnly?: boolean }
): CalendarLinkedContext {
  const shared = useContext(SharedCalendarLinkedContext);
  const gid = (gradeId ?? '').trim();
  const localOnly = options?.localOnly === true;
  const useShared =
    !localOnly &&
    shared != null &&
    shared.workspaceId === workspaceId &&
    shared.gradeId === gid &&
    gid !== '';
  const local = useCalendarLinkedContextResolved(workspaceId, gid, !useShared, { localOnly });
  return useShared ? shared.ctx : local;
}
